import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import LevelUpModal from "@/components/LevelUpModal";

interface GamificationContextType {
  showXpGain: (amount: number, message?: string) => void;
  showLevelUp: (newLevel: number) => void;
  handleGamificationResponse: (data: { xpGained?: number; leveledUp?: boolean; newLevel?: number; streakBonus?: boolean; jointBonus?: boolean }) => void;
}

const GamificationContext = createContext<GamificationContextType | null>(null);

// Celebration levels: when the user crosses one of these, the full
// LevelUpModal pops with confetti. Every other level-up is just a small
// toast — the user gets the dopamine but not the screen interruption.
// Aligned with the badge thresholds in backend/services/badges.py
// (BADGE_LEVEL_5, _10, _25) plus 50, 75, and every 25 after 100.
// Founder report 2026-06-04: every-level modal was firing on the order
// of a few times per day at higher levels, which is more friction than
// celebration.
function isMilestoneLevel(level: number): boolean {
  if (level === 5 || level === 10 || level === 25 || level === 50 || level === 75) return true;
  if (level >= 100 && level % 25 === 0) return true;
  return false;
}

export function GamificationProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [levelUpModal, setLevelUpModal] = useState<{ open: boolean; level: number }>({ open: false, level: 1 });

  const showXpGain = useCallback((amount: number, message?: string) => {
    toast({
      title: `+${amount} XP`,
      description: message,
      duration: 2500,
    });
  }, [toast]);

  const showLevelUp = useCallback((newLevel: number) => {
    // Milestones get the full modal. Non-milestones get a short
    // celebratory toast so the user still feels the win without losing
    // their place in the flow.
    if (isMilestoneLevel(newLevel)) {
      setLevelUpModal({ open: true, level: newLevel });
    } else {
      toast({
        title: `🎉 Level ${newLevel}!`,
        description: "Keep going — next milestone is on the way.",
        duration: 3000,
      });
    }
  }, [toast]);

  const handleGamificationResponse = useCallback((data: { xpGained?: number; leveledUp?: boolean; newLevel?: number; streakBonus?: boolean; jointBonus?: boolean }) => {
    if (data.xpGained && data.xpGained > 0) {
      let message = "";
      if (data.jointBonus) {
        message = "Togetherness bonus! +5 XP";
      } else if (data.streakBonus) {
        message = "3-day streak bonus!";
      }
      showXpGain(data.xpGained, message);
    }

    if (data.leveledUp && data.newLevel) {
      setTimeout(() => {
        showLevelUp(data.newLevel!);
      }, 500);
    }
  }, [showXpGain, showLevelUp]);

  return (
    <GamificationContext.Provider value={{ showXpGain, showLevelUp, handleGamificationResponse }}>
      {children}
      <LevelUpModal
        open={levelUpModal.open}
        onClose={() => setLevelUpModal({ open: false, level: levelUpModal.level })}
        newLevel={levelUpModal.level}
      />
    </GamificationContext.Provider>
  );
}

export function useGamification() {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error("useGamification must be used within a GamificationProvider");
  }
  return context;
}
